import { useCallback, useEffect, useRef } from 'react';

export type RoutableView = 'home' | 'editor' | 'canvas' | 'detail' | 'tools' | 'featured' | 'bongworks' | 'content';

export interface InitialRoute {
  view: RoutableView;
  projectId: string | null;
  /** featured 视图的 query 原串（如 `type=image&id=wf-01`），供刷新恢复预览作品；其他视图为空 */
  query?: string;
  /** content 视图的站点内容 slug（如 privacy-policy），供刷新恢复内容页 */
  slug?: string;
}

/**
 * 从 URL pathname 解析初始路由。
 * - `/project/:id` → editor 视图 + projectId
 * - `/canvas`      → canvas 视图
 * - `/detail`      → detail 视图
 * - `/tools`       → tools 视图
 * - `/bongworks`   → bongworks 视图（Bong作品独立页，用户上传的全部作品）
 * - `/featured`    → featured 视图（作品预览，query 携带 type/id）
 * - `/content`     → content 视图（站点内容页，query 携带 slug）
 * - 其他（含 `/`） → home 视图
 */
export function parseInitialRoute(): InitialRoute {
  const path = window.location.pathname;
  const projectMatch = path.match(/^\/project\/([\w-]+)\/?$/);
  if (projectMatch) {
    return { view: 'editor', projectId: projectMatch[1] };
  }
  if (path === '/canvas') return { view: 'canvas', projectId: null };
  if (path === '/detail') return { view: 'detail', projectId: null };
  if (path === '/tools') return { view: 'tools', projectId: null };
  if (path === '/bongworks') return { view: 'bongworks', projectId: null };
  if (path === '/featured') {
    return { view: 'featured', projectId: null, query: window.location.search.replace(/^\?/, '') || undefined };
  }
  if (path === '/content') {
    const slug = new URLSearchParams(window.location.search).get('slug') || '';
    return { view: 'content', projectId: null, slug: slug || undefined };
  }
  return { view: 'home', projectId: null };
}

/**
 * 根据 view + projectId 生成目标 URL pathname。
 * @param query featured 视图附加的 query 原串（不含 `?`）
 */
function buildPathname(view: RoutableView, projectId: string | null, query?: string): string {
  if (view === 'editor' && projectId) return `/project/${projectId}`;
  if (view === 'canvas') return '/canvas';
  if (view === 'detail') return '/detail';
  if (view === 'tools') return '/tools';
  if (view === 'bongworks') return '/bongworks';
  if (view === 'featured') return query ? `/featured?${query}` : '/featured';
  if (view === 'content') return query ? `/content?${query}` : '/content';
  return '/';
}

interface UseUrlRoutingResult {
  /** 视图/项目变化时调用，同步到 URL（pushState，进入历史栈）；featured 视图传 query 携带作品定位参数 */
  syncUrl: (view: RoutableView, projectId: string | null, query?: string) => void;
}

/**
 * 轻量 URL 路由同步 hook。
 *
 * 设计目标：
 * 1. 刷新页面时从 URL 恢复视图与项目 ID，避免被踢回首页
 * 2. 视图切换时同步 URL，支持浏览器前进/后退
 * 3. 不引入 react-router，最小改动接入现有状态机架构
 *
 * 使用方式：
 *   // 1. 用 parseInitialRoute() 初始化 useState（惰性求值，仅解析一次）
 *   // 2. 在所有依赖声明后调用 useUrlRouting(handlePopState) 获取 syncUrl
 *   // 3. 在视图变化 effect 中调用 syncUrl(currentView, currentProjectId)
 */
export function useUrlRouting(onPopState?: (route: InitialRoute) => void): UseUrlRoutingResult {
  // 用 ref 持有最新的 onPopState，避免回调变化时反复注册/卸载 popstate 监听器
  const onPopStateRef = useRef(onPopState);
  onPopStateRef.current = onPopState;

  const syncUrl = useCallback((view: RoutableView, projectId: string | null, query?: string) => {
    // editor 视图但尚无 projectId 时不更新 URL（避免在项目加载过程中把 URL 重置为 /）
    if (view === 'editor' && !projectId) return;
    const target = buildPathname(view, projectId, query);
    // featured 的 target 含 query，pathname+search 一起比较
    const current = window.location.pathname + window.location.search;
    if (current !== target) {
      window.history.pushState({ view, projectId, query }, '', target);
    }
  }, []);

  useEffect(() => {
    const handler = () => {
      const route = parseInitialRoute();
      onPopStateRef.current?.(route);
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  return { syncUrl };
}
